import React, { useMemo, useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { METRIC_CONFIG } from './SideNav';

const styles = {
    // ... all other styles are unchanged
    container: { 
        fontFamily: 'Inter, system-ui, Arial, sans-serif', 
        backgroundColor: '#ffffff', 
        borderRadius: '16px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        border: '1px solid #f1f5f9', 
        padding: '24px',
        overflow: 'hidden'
    },
    header: { 
        marginBottom: '20px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap', 
        gap: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid #f1f5f9'
    },
    title: { 
        margin: 0, 
        color: '#111827', 
        fontWeight: 700, 
        fontSize: '1.5rem',
        letterSpacing: '-0.025em'
    },
    controls: { 
        display: 'flex', 
        gap: '4px', 
        alignItems: 'center', 
        backgroundColor: '#f8fafc', 
        padding: '6px', 
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
    },
    controlLabel: { 
        fontWeight: 600, 
        fontSize: '0.875rem', 
        padding: '0 12px', 
        color: '#64748b' 
    },
    controlButton: { 
        padding: '8px 16px', 
        border: 'none', 
        background: 'transparent', 
        cursor: 'pointer', 
        borderRadius: '8px', 
        fontWeight: 500, 
        color: '#64748b',
        fontSize: '0.875rem',
        transition: 'all 0.2s ease-in-out'
    },
    controlButtonActive: { 
        background: '#ffffff', 
        color: '#0f172a', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        fontWeight: 600
    },
    // Updated error style for a better presentation
    errorContainer: { 
        color: '#991b1b', // Darker red for text
        backgroundColor: '#fef2f2', 
        border: '1px solid #fecaca', 
        padding: '20px', 
        borderRadius: '12px', 
        marginTop: '20px',
    },
    errorTitle: {
        margin: '0 0 10px 0',
        fontWeight: 700,
        fontSize: '1rem',
        color: '#b91c1c'
    },
    errorMessage: {
        margin: 0,
        fontSize: '0.9rem',
        lineHeight: '1.5',
    },
    loading: { 
        color: '#64748b', 
        textAlign: 'center', 
        padding: '60px 20px',
        fontSize: '1rem'
    },
    // ... rest of styles are unchanged
    tableWrapper: { 
        width: '100%', 
        overflowX: 'auto', 
        borderRadius: '12px', 
        border: '1px solid #e2e8f0', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    table: { 
        width: '100%', 
        borderCollapse: 'separate', 
        borderSpacing: 0, 
        backgroundColor: '#fff',
        fontSize: '0.875rem'
    },
    thead: { 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
    },
    th: { 
        position: 'sticky', 
        top: 0, 
        backgroundColor: '#f8fafc', 
        borderBottom: '1px solid #e2e8f0', 
        padding: '16px 12px', 
        textAlign: 'center', 
        fontWeight: 700, 
        whiteSpace: 'nowrap',
        color: '#374151',
        fontSize: '0.875rem'
    },
    td: { 
        borderBottom: '1px solid #f1f5f9', 
        padding: '14px 12px', 
        textAlign: 'center', 
        verticalAlign: 'middle',
        color: '#374151'
    },
    adNameCell: { 
        fontWeight: 600, 
        textAlign: 'left', 
        color: '#0f172a',
        fontSize: '0.875rem'
    },
    noDataCell: { 
        color: '#94a3b8',
        fontStyle: 'italic'
    },
    trEven: { 
        backgroundColor: '#f8fafc' 
    },
    trHover: {
        transition: 'background-color 0.2s ease',
        cursor: 'pointer'
    },
    tfoot: { 
        position: 'sticky', 
        bottom: 0, 
        zIndex: 5 
    },
    tfootCell: { 
        position: 'sticky', 
        bottom: 0, 
        padding: '14px 12px', 
        textAlign: 'center', 
        fontWeight: 700, 
        background: '#f1f5f9', 
        borderTop: '2px solid #e2e8f0', 
        fontSize: '0.8rem',
        color: '#0f172a'
    },
    tfootNameCell: { 
        position: 'sticky', 
        bottom: 0, 
        padding: '14px 12px', 
        textAlign: 'left', 
        fontWeight: 800, 
        background: '#f1f5f9', 
        borderTop: '2px solid #e2e8f0', 
        color: '#0f172a', 
        fontSize: '0.8rem'
    },
    noDataMessage: { 
        textAlign: 'center', 
        padding: '60px 20px', 
        backgroundColor: '#f8fafc', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px', 
        color: '#64748b', 
        marginTop: '20px',
        fontSize: '1rem'
    },
    exportButtonContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    },
    exportButton: {
        padding: '8px 16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: '0.875rem',
        transition: 'all 0.2s ease',
        borderRight: '1px solid #60a5fa',
        whiteSpace: 'nowrap'
    },
    exportDropdownButton: {
        padding: '8px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px',
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: '0.875rem',
        transition: 'all 0.2s ease',
    },
    exportMenu: {
        position: 'absolute',
        top: '110%',
        right: 0,
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        zIndex: 100,
        overflow: 'hidden',
        minWidth: '240px'
    },
    exportMenuItem: {
        padding: '10px 16px',
        border: 'none',
        background: 'transparent',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '0.875rem',
        color: '#374151'
    }
};

const CellDisplay = ({ cellData, scoreType, displayType }) => {
    // ... No changes needed in this component
    if (!cellData) {
        return <span style={styles.noDataCell}>-</span>;
    }
    let value = null;
    if (displayType === 'score') {
        value = scoreType === 'topBox' ? cellData.TopBoxScore : cellData.Top2BoxScore;
        return value !== null ? (<span style={{ color: '#0f172a', fontWeight: 500 }}>{value.toFixed(1)}</span>) : <span style={styles.noDataCell}>-</span>;
    }
    if (displayType === 'count') {
        value = scoreType === 'topBox' ? cellData.TopBoxCount : cellData.Top2BoxCount;
    }
    if (displayType === 'baseSize') {
        value = cellData.ValidN !== undefined ? cellData.ValidN : cellData.BaseSize;
    }
    return value !== null ? (<span style={{ color: '#475569', fontWeight: 400 }}>{value.toLocaleString()}</span>) : <span style={styles.noDataCell}>-</span>;
};

const AdRecallAllFiles = ({
    loading,
    error,
    data = [],
    percentiles = {},
    metric,
    scoreType,
    displayType,
    setDisplayType,
    onExportAllMetrics,
    isExporting,
    bannerStructure = [],
    nestedBannerStructure = [],
    totalN = 0
}) => {

    const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
    const exportMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setIsExportMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [exportMenuRef]);

    const pivotData = useMemo(() => {
        if (!data || data.length === 0) return { headers: [], rows: [] };

        const rows = data.map(ad => ({
            AdsName: ad.AdsName,
            scores: ad.scores || {}
        }));

        // Build headers from banner structure for export
        const headers = [];

        // Add Total column
        headers.push({ key: 'Total', label: 'Total', type: 'total' });

        // Add simple banner break columns
        bannerStructure.forEach(banner => {
            banner.breaks.forEach(breakValue => {
                headers.push({
                    key: banner.key,
                    breakValue: breakValue,
                    label: `${banner.label} - ${breakValue}`,
                    type: 'simple'
                });
            });
        });

        // Add nested banner columns
        nestedBannerStructure.forEach(nested => {
            nested.breaks.forEach(parentBreak => {
                parentBreak.children.forEach(childValue => {
                    headers.push({
                        key: nested.key,
                        parentValue: parentBreak.parent_value,
                        childValue: childValue,
                        label: `${parentBreak.parent_value} - ${childValue}`,
                        type: 'nested'
                    });
                });
            });
        });

        return { headers, rows };
    }, [data, bannerStructure, nestedBannerStructure]);

    // Helper function to get cell data based on header type
    const getCellData = (row, header) => {
        if (header.type === 'total') {
            return row.scores?.Total;
        } else if (header.type === 'simple') {
            return row.scores?.[header.key]?.[header.breakValue];
        } else if (header.type === 'nested') {
            return row.scores?.[header.key]?.[header.parentValue]?.[header.childValue];
        }
        return null;
    };

    // Helper function to get percentile data based on header type
    const getPercentileData = (header, scoreKey, pKey) => {
        if (header.type === 'total') {
            return percentiles?.Total?.[scoreKey]?.[pKey];
        } else if (header.type === 'simple') {
            return percentiles?.[header.key]?.[header.breakValue]?.[scoreKey]?.[pKey];
        } else if (header.type === 'nested') {
            return percentiles?.[header.key]?.[header.parentValue]?.[header.childValue]?.[scoreKey]?.[pKey];
        }
        return undefined;
    };

    // Helper to get value from cell data based on displayType
    const getCellValue = (cellData, displayType, scoreType) => {
        if (!cellData) return '-';
        const isTopBox = scoreType === 'topBox';

        if (displayType === 'score') {
            const val = isTopBox ? cellData.TopBoxScore : cellData.Top2BoxScore;
            return val !== null && val !== undefined ? val.toFixed(1) : '-';
        } else if (displayType === 'count') {
            const val = isTopBox ? cellData.TopBoxCount : cellData.Top2BoxCount;
            return val !== null && val !== undefined ? val : '-';
        } else if (displayType === 'baseSize') {
            const val = cellData.ValidN !== undefined ? cellData.ValidN : cellData.BaseSize;
            return val !== null && val !== undefined ? val : '-';
        }
        return '-';
    };

    const handleExport = (exportType) => {
        if (!pivotData.rows || pivotData.rows.length === 0) {
            alert("No data to export.");
            return;
        }

        const metricTitle = METRIC_CONFIG[metric]?.title.replace(/\s+/g, '_') || 'Analysis';
        const displayLabel = displayType === 'score' ? 'Score' : (displayType === 'count' ? 'Count' : 'Valid_N');
        let fileName = `${metricTitle}_${displayLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const isTopBox = scoreType === 'topBox';
        const scoreKey = isTopBox ? 'TopBoxScore' : 'Top2BoxScore';

        // Build structured export with merged headers
        const aoa = []; // Array of arrays for Excel
        const merges = []; // Cell merge ranges
        let colWidths = [{ wch: 40 }]; // Column widths

        // Determine if we have nested banners
        const hasNestedBanners = nestedBannerStructure && nestedBannerStructure.length > 0;
        const numHeaderRows = hasNestedBanners ? 3 : (bannerStructure.length > 0 ? 2 : 1);

        // Build header rows
        const headerRow1 = ['Ad Name'];
        const headerRow2 = [''];
        const headerRow3 = [''];

        let colIndex = 1; // Start after Ad Name column

        // Add Total column
        headerRow1.push('Total');
        headerRow2.push('');
        headerRow3.push('');
        if (numHeaderRows > 1) {
            merges.push({ s: { r: 0, c: colIndex }, e: { r: numHeaderRows - 1, c: colIndex } }); // Merge Total across header rows
        }
        colWidths.push({ wch: 12 });
        colIndex++;

        // Add simple banner columns
        bannerStructure.forEach(banner => {
            const startCol = colIndex;
            banner.breaks.forEach(breakValue => {
                headerRow1.push(banner.label);
                headerRow2.push(breakValue);
                headerRow3.push('');
                colWidths.push({ wch: 12 });
                colIndex++;
            });
            // Merge banner label across its breaks (row 1)
            if (banner.breaks.length > 1) {
                merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: colIndex - 1 } });
            }
            // For simple banners in 3-row header, merge row 2 and 3 for each break
            if (hasNestedBanners) {
                for (let i = startCol; i < colIndex; i++) {
                    merges.push({ s: { r: 1, c: i }, e: { r: 2, c: i } });
                }
            }
        });

        // Add nested banner columns
        nestedBannerStructure.forEach(nested => {
            const nestedStartCol = colIndex;

            nested.breaks.forEach(parentBreak => {
                const parentStartCol = colIndex;

                parentBreak.children.forEach(childValue => {
                    headerRow1.push(nested.parent_label);
                    headerRow2.push(parentBreak.parent_value);
                    headerRow3.push(childValue);
                    colWidths.push({ wch: 12 });
                    colIndex++;
                });

                // Merge parent value across its children (row 2)
                if (parentBreak.children.length > 1) {
                    merges.push({ s: { r: 1, c: parentStartCol }, e: { r: 1, c: colIndex - 1 } });
                }
            });

            // Merge nested banner label across all its columns (row 1)
            if (colIndex > nestedStartCol) {
                merges.push({ s: { r: 0, c: nestedStartCol }, e: { r: 0, c: colIndex - 1 } });
            }
        });

        // Merge Ad Name cell across header rows
        if (numHeaderRows > 1) {
            merges.push({ s: { r: 0, c: 0 }, e: { r: numHeaderRows - 1, c: 0 } });
        }

        // Add header rows to aoa
        aoa.push(headerRow1);
        if (numHeaderRows >= 2) aoa.push(headerRow2);
        if (numHeaderRows >= 3) aoa.push(headerRow3);

        // Add Total (n) row
        const totalNRow = ['Total (n)', totalN];
        bannerStructure.forEach(banner => {
            banner.breaks.forEach(breakValue => {
                totalNRow.push(banner.totals?.[breakValue] || 0);
            });
        });
        nestedBannerStructure.forEach(nested => {
            nested.breaks.forEach(parentBreak => {
                parentBreak.children.forEach(childValue => {
                    totalNRow.push(parentBreak.totals?.[childValue] || 0);
                });
            });
        });
        aoa.push(totalNRow);

        // Add data rows - use current displayType
        pivotData.rows.forEach(row => {
            const dataRow = [row.AdsName];

            // Total
            const totalData = row.scores?.Total;
            dataRow.push(getCellValue(totalData, displayType, scoreType));

            // Simple banner breaks
            bannerStructure.forEach(banner => {
                banner.breaks.forEach(breakValue => {
                    const cellData = row.scores?.[banner.key]?.[breakValue];
                    dataRow.push(getCellValue(cellData, displayType, scoreType));
                });
            });

            // Nested banner breaks
            nestedBannerStructure.forEach(nested => {
                nested.breaks.forEach(parentBreak => {
                    parentBreak.children.forEach(childValue => {
                        const cellData = row.scores?.[nested.key]?.[parentBreak.parent_value]?.[childValue];
                        dataRow.push(getCellValue(cellData, displayType, scoreType));
                    });
                });
            });

            aoa.push(dataRow);
        });

        // Add empty row before percentiles (only for score display)
        if (displayType === 'score') {
            aoa.push([]);

            // Add percentile rows
            const percentileKeys = ['Average', '90 Percentile', '75 Percentile', '50 Percentile'];
            percentileKeys.forEach(pKey => {
                const pRow = [pKey];

                // Total percentile
                const totalPVal = percentiles?.Total?.[scoreKey]?.[pKey];
                pRow.push(totalPVal !== undefined ? totalPVal.toFixed(1) : '-');

                // Simple banner percentiles
                bannerStructure.forEach(banner => {
                    banner.breaks.forEach(breakValue => {
                        const pVal = percentiles?.[banner.key]?.[breakValue]?.[scoreKey]?.[pKey];
                        pRow.push(pVal !== undefined ? pVal.toFixed(1) : '-');
                    });
                });

                // Nested banner percentiles
                nestedBannerStructure.forEach(nested => {
                    nested.breaks.forEach(parentBreak => {
                        parentBreak.children.forEach(childValue => {
                            const pVal = percentiles?.[nested.key]?.[parentBreak.parent_value]?.[childValue]?.[scoreKey]?.[pKey];
                            pRow.push(pVal !== undefined ? pVal.toFixed(1) : '-');
                        });
                    });
                });

                aoa.push(pRow);
            });
        }

        // Create worksheet from array of arrays
        const worksheet = XLSX.utils.aoa_to_sheet(aoa);
        worksheet['!cols'] = colWidths;
        worksheet['!merges'] = merges;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
        XLSX.writeFile(workbook, fileName);
    };

    // --- MODIFIED ERROR AND LOADING STATES ---
    if (loading) {
        return (
            <div style={styles.container}>
                <p style={styles.loading}>Updating data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <h3 style={styles.errorTitle}>Unable to Load Data</h3>
                    <p style={styles.errorMessage}>{error}</p>
                </div>
            </div>
        );
    }
    // --- END MODIFICATION ---

    const scoreTypeKey = scoreType === 'topBox' ? 'TopBoxScore' : 'Top2BoxScore';

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h1 style={{...styles.title, marginRight: '16px'}}>{METRIC_CONFIG[metric]?.title || 'Analysis Table'}</h1>

                    {/* Export buttons on the left, near title */}
                    <div style={{...styles.exportButtonContainer, ...(isExporting && { opacity: 0.5, pointerEvents: 'none' })}} ref={exportMenuRef}>
                        <button
                            style={styles.exportButton}
                            onClick={() => handleExport('comprehensive')}
                            disabled={isExporting}
                        >
                            Export
                        </button>

                        <button
                            style={styles.exportDropdownButton}
                            onClick={() => setIsExportMenuOpen(prev => !prev)}
                            disabled={isExporting}
                        >
                            &#9662;
                        </button>

                        {isExportMenuOpen && (
                            <div style={{...styles.exportMenu, left: 0, right: 'auto'}}>
                                <button
                                    style={styles.exportMenuItem}
                                    onClick={() => {
                                        onExportAllMetrics();
                                        setIsExportMenuOpen(false);
                                    }}
                                >
                                    Export All Performance Metrics
                                </button>
                                <hr style={{ margin: '4px 0', border: 0, borderTop: '1px solid #f1f5f9' }} />
                                <button
                                    style={styles.exportMenuItem}
                                    onClick={() => {
                                        handleExport('allForScoreType');
                                        setIsExportMenuOpen(false);
                                    }}
                                >
                                    Export {scoreType === 'topBox' ? 'Top Box' : 'Top 2 Box'} (Current Metric)
                                </button>
                                <button
                                    style={styles.exportMenuItem}
                                    onClick={() => {
                                        handleExport('currentView');
                                        setIsExportMenuOpen(false);
                                    }}
                                >
                                    Export Current View ({displayType === 'score' ? 'Score %' : displayType === 'count' ? 'Count' : 'Base n'})
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* View controls on the right */}
                <div style={styles.controls}>
                    <span style={styles.controlLabel}>View:</span>
                    <button
                        style={displayType === 'score' ? {...styles.controlButton, ...styles.controlButtonActive} : styles.controlButton}
                        onClick={() => setDisplayType('score')}>Score %</button>
                    <button
                        style={displayType === 'count' ? {...styles.controlButton, ...styles.controlButtonActive} : styles.controlButton}
                        onClick={() => setDisplayType('count')}>Count</button>
                    <button
                        style={displayType === 'baseSize' ? {...styles.controlButton, ...styles.controlButtonActive} : styles.controlButton}
                        onClick={() => setDisplayType('baseSize')}>Valid N</button>
                </div>
            </div>

            {data.length > 0 ? (
                <div style={styles.tableWrapper}>
                   <table style={styles.table}>
                        <thead style={styles.thead}>
                            {/* Determine header row count based on banners */}
                            {nestedBannerStructure.length > 0 ? (
                                <>
                                    {/* Row 1: Parent banner labels */}
                                    <tr>
                                        <th style={{...styles.th, textAlign: 'left', minWidth: '250px', borderRight: '1px solid #e2e8f0', borderBottom: 'none' }} rowSpan={3}>Ad Name</th>
                                        <th style={{...styles.th, borderBottom: 'none', backgroundColor: '#e2e8f0', fontWeight: 800 }} rowSpan={3}>Total</th>
                                        {/* Simple banners */}
                                        {bannerStructure.map(banner => (
                                            <th
                                                key={banner.key}
                                                colSpan={banner.breaks.length}
                                                rowSpan={2}
                                                style={{
                                                    ...styles.th,
                                                    borderBottom: 'none',
                                                    backgroundColor: '#e2e8f0',
                                                    fontWeight: 800,
                                                    borderLeft: '2px solid #cbd5e1'
                                                }}
                                            >
                                                {banner.label}
                                            </th>
                                        ))}
                                        {/* Nested banners - parent labels */}
                                        {nestedBannerStructure.map(nested => (
                                            <th
                                                key={nested.key}
                                                colSpan={nested.breaks.reduce((sum, b) => sum + b.children.length, 0)}
                                                style={{
                                                    ...styles.th,
                                                    borderBottom: 'none',
                                                    backgroundColor: '#dbeafe',
                                                    fontWeight: 800,
                                                    borderLeft: '2px solid #3b82f6'
                                                }}
                                            >
                                                {nested.parent_label}
                                            </th>
                                        ))}
                                    </tr>
                                    {/* Row 2: Parent break values (for nested) */}
                                    <tr>
                                        {/* Nested banners - parent break values */}
                                        {nestedBannerStructure.map(nested => (
                                            nested.breaks.map((parentBreak, parentIdx) => (
                                                <th
                                                    key={`${nested.key}_${parentBreak.parent_value}`}
                                                    colSpan={parentBreak.children.length}
                                                    style={{
                                                        ...styles.th,
                                                        borderBottom: 'none',
                                                        backgroundColor: '#eff6ff',
                                                        fontWeight: 700,
                                                        borderLeft: parentIdx === 0 ? '2px solid #3b82f6' : '1px solid #93c5fd'
                                                    }}
                                                >
                                                    {parentBreak.parent_value}
                                                </th>
                                            ))
                                        ))}
                                    </tr>
                                    {/* Row 3: Child values (Wave values) for nested + simple banner break values */}
                                    <tr>
                                        {/* Simple banner break values */}
                                        {bannerStructure.map(banner => (
                                            banner.breaks.map((breakValue, breakIdx) => (
                                                <th
                                                    key={`${banner.key}_${breakValue}`}
                                                    style={{
                                                        ...styles.th,
                                                        minWidth: '70px',
                                                        fontSize: '0.8rem',
                                                        ...(breakIdx === 0 ? { borderLeft: '2px solid #cbd5e1' } : {})
                                                    }}
                                                >
                                                    {breakValue}
                                                </th>
                                            ))
                                        ))}
                                        {/* Nested banner child values */}
                                        {nestedBannerStructure.map(nested => (
                                            nested.breaks.map((parentBreak, parentIdx) => (
                                                parentBreak.children.map((childValue, childIdx) => (
                                                    <th
                                                        key={`${nested.key}_${parentBreak.parent_value}_${childValue}`}
                                                        style={{
                                                            ...styles.th,
                                                            minWidth: '70px',
                                                            fontSize: '0.8rem',
                                                            backgroundColor: childValue === 'Total' ? '#fef3c7' : '#ffffff',
                                                            fontWeight: childValue === 'Total' ? 700 : 500,
                                                            borderLeft: (parentIdx === 0 && childIdx === 0) ? '2px solid #3b82f6' :
                                                                       (childIdx === 0 ? '1px solid #93c5fd' : undefined)
                                                        }}
                                                    >
                                                        {childValue}
                                                    </th>
                                                ))
                                            ))
                                        ))}
                                    </tr>
                                </>
                            ) : (
                                <>
                                    {/* Original simple banner headers */}
                                    {bannerStructure.length > 0 && (
                                        <tr>
                                            <th style={{...styles.th, textAlign: 'left', minWidth: '250px', borderRight: '1px solid #e2e8f0', borderBottom: 'none' }} rowSpan={2}>Ad Name</th>
                                            <th style={{...styles.th, borderBottom: 'none', backgroundColor: '#e2e8f0', fontWeight: 800 }} rowSpan={2}>Total</th>
                                            {bannerStructure.map(banner => (
                                                <th
                                                    key={banner.key}
                                                    colSpan={banner.breaks.length}
                                                    style={{
                                                        ...styles.th,
                                                        borderBottom: 'none',
                                                        backgroundColor: '#e2e8f0',
                                                        fontWeight: 800,
                                                        borderLeft: '2px solid #cbd5e1'
                                                    }}
                                                >
                                                    {banner.label}
                                                </th>
                                            ))}
                                        </tr>
                                    )}
                                    {/* Column headers row */}
                                    <tr>
                                        {bannerStructure.length === 0 && (
                                            <>
                                                <th style={{...styles.th, textAlign: 'left', minWidth: '250px', borderRight: '1px solid #e2e8f0' }}>Ad Name</th>
                                                <th style={{...styles.th, minWidth: '80px' }}>Total</th>
                                            </>
                                        )}
                                        {bannerStructure.map(banner => (
                                            banner.breaks.map((breakValue, breakIdx) => (
                                                <th
                                                    key={`${banner.key}_${breakValue}`}
                                                    style={{
                                                        ...styles.th,
                                                        minWidth: '80px',
                                                        ...(breakIdx === 0 ? { borderLeft: '2px solid #cbd5e1' } : {})
                                                    }}
                                                >
                                                    {breakValue}
                                                </th>
                                            ))
                                        ))}
                                    </tr>
                                </>
                            )}
                        </thead>
                        <tbody>
                            {/* Total (n) row showing sample sizes */}
                            <tr style={{ backgroundColor: '#f0fdf4', borderBottom: '2px solid #86efac' }}>
                                <td style={{...styles.td, fontWeight: 700, color: '#166534', textAlign: 'left', borderRight: '1px solid #e2e8f0'}}>Total (n)</td>
                                <td style={{...styles.td, fontWeight: 700, color: '#166534'}}>{totalN.toLocaleString()}</td>
                                {/* Simple banner totals */}
                                {bannerStructure.map(banner => (
                                    banner.breaks.map((breakValue, breakIdx) => (
                                        <td
                                            key={`total_${banner.key}_${breakValue}`}
                                            style={{
                                                ...styles.td,
                                                fontWeight: 600,
                                                color: '#166534',
                                                ...(breakIdx === 0 ? { borderLeft: '2px solid #e2e8f0' } : {})
                                            }}
                                        >
                                            {(banner.totals?.[breakValue] || 0).toLocaleString()}
                                        </td>
                                    ))
                                ))}
                                {/* Nested banner totals */}
                                {nestedBannerStructure.map(nested => (
                                    nested.breaks.map((parentBreak, parentIdx) => (
                                        parentBreak.children.map((childValue, childIdx) => (
                                            <td
                                                key={`total_${nested.key}_${parentBreak.parent_value}_${childValue}`}
                                                style={{
                                                    ...styles.td,
                                                    fontWeight: 600,
                                                    color: '#166534',
                                                    backgroundColor: childValue === 'Total' ? '#dcfce7' : '#f0fdf4',
                                                    borderLeft: (parentIdx === 0 && childIdx === 0) ? '2px solid #93c5fd' :
                                                               (childIdx === 0 ? '1px solid #dbeafe' : undefined)
                                                }}
                                            >
                                                {(parentBreak.totals?.[childValue] || 0).toLocaleString()}
                                            </td>
                                        ))
                                    ))
                                ))}
                            </tr>
                            {pivotData.rows.map((row, index) => (
                                <tr key={row.AdsName} style={{...(index % 2 === 0 ? {} : styles.trEven), ...styles.trHover}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f8fafc'}>
                                    <td style={{...styles.td, ...styles.adNameCell, borderRight: '1px solid #e2e8f0'}}>{row.AdsName}</td>
                                    {/* Total column */}
                                    <td style={styles.td}>
                                        <CellDisplay cellData={row.scores.Total} scoreType={scoreType} displayType={displayType} />
                                    </td>
                                    {/* Simple banner break columns */}
                                    {bannerStructure.map(banner => (
                                        banner.breaks.map((breakValue, breakIdx) => (
                                            <td
                                                key={`${banner.key}_${breakValue}`}
                                                style={{
                                                    ...styles.td,
                                                    ...(breakIdx === 0 ? { borderLeft: '2px solid #e2e8f0' } : {})
                                                }}
                                            >
                                                <CellDisplay
                                                    cellData={row.scores[banner.key]?.[breakValue]}
                                                    scoreType={scoreType}
                                                    displayType={displayType}
                                                />
                                            </td>
                                        ))
                                    ))}
                                    {/* Nested banner columns */}
                                    {nestedBannerStructure.map(nested => (
                                        nested.breaks.map((parentBreak, parentIdx) => (
                                            parentBreak.children.map((childValue, childIdx) => (
                                                <td
                                                    key={`${nested.key}_${parentBreak.parent_value}_${childValue}`}
                                                    style={{
                                                        ...styles.td,
                                                        backgroundColor: childValue === 'Total' ? '#fffbeb' : undefined,
                                                        fontWeight: childValue === 'Total' ? 600 : undefined,
                                                        borderLeft: (parentIdx === 0 && childIdx === 0) ? '2px solid #93c5fd' :
                                                                   (childIdx === 0 ? '1px solid #dbeafe' : undefined)
                                                    }}
                                                >
                                                    <CellDisplay
                                                        cellData={row.scores[nested.key]?.[parentBreak.parent_value]?.[childValue]}
                                                        scoreType={scoreType}
                                                        displayType={displayType}
                                                    />
                                                </td>
                                            ))
                                        ))
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot style={styles.tfoot}>
                            {['Average', '90 Percentile', '75 Percentile', '50 Percentile'].map(pKey => (
                                <tr key={pKey}>
                                    <td style={{...styles.tfootNameCell, fontWeight: pKey === 'Average' ? 800 : 700, color: pKey === 'Average' ? '#0f172a' : '#374151'}}>{pKey}</td>
                                    {/* Total percentile */}
                                    <td style={{...styles.tfootCell, fontWeight: pKey === 'Average' ? 800 : 700}}>
                                        {percentiles.Total?.[scoreTypeKey]?.[pKey] !== undefined
                                            ? (<span style={{ color: '#0f172a' }}>{percentiles.Total[scoreTypeKey][pKey].toFixed(1)}</span>)
                                            : '-'}
                                    </td>
                                    {/* Simple banner break percentiles */}
                                    {bannerStructure.map(banner => (
                                        banner.breaks.map((breakValue, breakIdx) => {
                                            const pValue = percentiles[banner.key]?.[breakValue]?.[scoreTypeKey]?.[pKey];
                                            return (
                                                <td
                                                    key={`${banner.key}_${breakValue}_${pKey}`}
                                                    style={{
                                                        ...styles.tfootCell,
                                                        fontWeight: pKey === 'Average' ? 800 : 700,
                                                        ...(breakIdx === 0 ? { borderLeft: '2px solid #e2e8f0' } : {})
                                                    }}
                                                >
                                                    {pValue !== undefined && pValue !== null
                                                        ? (<span style={{ color: '#0f172a' }}>{pValue.toFixed(1)}</span>)
                                                        : '-'}
                                                </td>
                                            );
                                        })
                                    ))}
                                    {/* Nested banner percentiles */}
                                    {nestedBannerStructure.map(nested => (
                                        nested.breaks.map((parentBreak, parentIdx) => (
                                            parentBreak.children.map((childValue, childIdx) => {
                                                const pValue = percentiles[nested.key]?.[parentBreak.parent_value]?.[childValue]?.[scoreTypeKey]?.[pKey];
                                                return (
                                                    <td
                                                        key={`${nested.key}_${parentBreak.parent_value}_${childValue}_${pKey}`}
                                                        style={{
                                                            ...styles.tfootCell,
                                                            fontWeight: pKey === 'Average' ? 800 : 700,
                                                            borderLeft: (parentIdx === 0 && childIdx === 0) ? '2px solid #93c5fd' :
                                                                       (childIdx === 0 ? '1px solid #dbeafe' : undefined)
                                                        }}
                                                    >
                                                        {pValue !== undefined && pValue !== null
                                                            ? (<span style={{ color: '#0f172a' }}>{pValue.toFixed(1)}</span>)
                                                            : '-'}
                                                    </td>
                                                );
                                            })
                                        ))
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    </table>
                </div>
            ) : (
                <div style={styles.noDataMessage}>No data available for the selected filters.</div>
            )}
        </div>
    );
};

export default AdRecallAllFiles;